// KRNOS screen help (the "?" on KRNOS). Plain words; keep it in step with the screen
// (apps/mobile/src/tools/KrnosTool.tsx): button labels, form fields, alert wording.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const krnosHelp: ScreenHelpContent = {
  title: 'KRNOS',
  intro:
    'KRNOS is your schedule: a calendar for the things that happen at a fixed time, like appointments, classes and the weekly rhythm of your life. Put those in first, then fit the rest of your work around them.',
  sections: [
    {
      heading: 'The top of the screen',
      items: [
        { term: '+ Add Event', text: 'opens the form for a new event on the day you are looking at.' },
        { term: 'TSK', text: 'a switch. When it is on, events that repeat are hidden, so you only see one-off events. Tap it again to bring them back.' },
        { term: '‹  Today  ›', text: 'go back, jump to today, or go forward. It moves a week, a day or a month, depending on the view. The dates you are looking at are written on the right.' },
        { term: 'Weekly / Daily / Monthly', text: 'choose how much you see.' },
      ],
    },
    {
      heading: 'Weekly and Daily',
      body: [
        'A grid of time slots. The week runs Sunday to Saturday, and today is highlighted. The times stay in place on the left; swipe sideways to see more days and up or down to see more hours. The hours shown and the size of each slot come from your Calendar settings.',
        'Each event is a coloured block. A ⟳ in front of the name means it repeats. Events at the same time sit side by side.',
        'Tap an empty slot to add an event at that day and time. Tap an event to see its details. If an event falls outside the hours shown, a "+N outside hours" tag appears at the bottom of that day. Tap it to see them.',
      ],
    },
    {
      heading: 'Monthly',
      body: [
        'The whole month at a glance, with the first few events of each day listed by time and name, and "+N more" when there are more. Tap a day to open it in Daily view.',
      ],
    },
    {
      heading: 'An event\'s details',
      body: [
        'Tapping an event shows its name, date, start and end time, whether it repeats (and until when), and any notes. Close puts it away, Edit opens the form and Delete removes it.',
      ],
    },
    {
      heading: 'Adding or editing an event',
      body: [
        'Nothing is saved until you press Add Event (or Save Changes when editing). Cancel throws your changes away.',
      ],
      items: [
        { term: 'Title', text: 'required.' },
        { term: 'Date', text: 'use ‹ and › to move the day.' },
        { term: 'Start (24h), End (24h)', text: 'type times like 09:00 and 17:30. If they are not in that form you will be told "Invalid time". A new event starts out one time slot long.' },
        { term: 'Notes', text: 'optional.' },
        { term: 'Color', text: 'tap one of the eight colours.' },
        { term: 'Repeat', text: 'None, Daily, Weekly, Monthly or Yearly.' },
        { term: 'Ends', text: 'shows up once you pick a repeat. Use ‹ and › to choose the last day, or leave it on "Never". Clear takes the end date off.' },
      ],
    },
    {
      heading: 'Repeating events',
      body: [
        'When you edit or delete one event in a series you are asked which to change: "This event only" or "This & future events". "This event only" leaves the rest of the series alone. "This & future events" ends the old series the day before and, for an edit, starts a new one from that day.',
        'Deleting an event that does not repeat asks "Delete event?" and takes it away straight after you confirm. There is no undo button here.',
      ],
    },
    {
      heading: 'Where else it shows',
      body: [
        'The KYRS verification calendar (the bell in KYRS, then Calendar) shows your events as busy time next to the auto-scheduled tasks.',
      ],
    },
  ],
};
